/**
 * My Reviews Page Route (V2)
 * Route: /profile/reviews
 */
import { createFileRoute } from '@tanstack/react-router';
import { ListPageTemplatePending } from '@/components/v2/page-template-pending';
import { myInstructorReviewsQuery, myStudioReviewsQuery } from '@/lib/queries/detail';
import { cacheFirst } from '@/lib/queries/cache-first';

export const Route = createFileRoute('/_authenticated/profile/reviews')({
  // Template-first pending: while this route's component chunk downloads, show the
  // destination's page shape instead of the full-page RoutePending mascot
  // (audit part 2: a detail click must switch immediately, skeletons included).
  // The title depends on the active tab, so it stays a skeleton block, but the
  // Instructors | Studios switcher above it renders in the pending.
  pendingComponent: () => <ListPageTemplatePending tabs />,
  loader: ({ context }) => {
    // Cursor-paginated (#423): the loader primes the first page so the
    // progressive <Await> shell renders without a flash; the component's
    // useInfiniteQuery reads the primed cache and pages via the cursor.
    // On client-side navigations the fetch goes browser → Go API directly;
    // only SSR pays the cookie-forwarding proxy hop (see authenticated-shared).
    // Each list is gated independently: a cached tab resolves instantly while
    // ensureInfiniteQueryData refreshes stale entries in the background; a
    // cold cache keeps the streamed fetch its own boundary renders against.
    const instructorRefresh = () =>
      context.queryClient.ensureInfiniteQueryData(myInstructorReviewsQuery());
    const studioRefresh = () => context.queryClient.ensureInfiniteQueryData(myStudioReviewsQuery());
    const { cached: cachedInstructor, stream: streamInstructor } = cacheFirst(
      context.queryClient,
      ['v2-my-reviews', 'instructors'],
      instructorRefresh
    );
    const { cached: cachedStudio, stream: streamStudio } = cacheFirst(
      context.queryClient,
      ['v2-my-reviews', 'studios'],
      studioRefresh
    );
    return {
      instructorReviewsPromise:
        cachedInstructor !== undefined ? Promise.resolve(cachedInstructor) : streamInstructor(),
      studioReviewsPromise:
        cachedStudio !== undefined ? Promise.resolve(cachedStudio) : streamStudio(),
    };
  },
});
